/**
 * RemoteRelayService — Firestore Cloud Relay
 *
 * Replaces the Vite dev-server middleware with Firestore as the message broker.
 * Both phone and desktop use this service:
 *
 *   Phone  → sendCommand()     → writes to  commands/{id}
 *   Desktop → onCommand()      → listens to commands/ where status == 'pending'
 *   Desktop → sendResponse()   → writes to  responses/{id}
 *   Phone  → onResponse()      → listens to responses/ for a given commandId
 *   Desktop → pushDesktopState() → writes to state doc
 *   Phone  → onDesktopState()   → listens to state doc
 *
 * Collection structure:
 *   users/{userId}/remote-relay/state                     ← desktop state doc
 *   users/{userId}/remote-relay-commands/{commandId}      ← phone writes, desktop reads
 *   users/{userId}/remote-relay-responses/{responseId}    ← desktop writes, phone reads
 *
 * Security: isOwner(userId) — only the authenticated user touches their data.
 * Works cross-network: cellular, different WiFi, anywhere with internet.
 */

import { serializeDispatchPayload } from '@/services/remote/dispatchPayload';
import {
    collection,
    doc,
    addDoc,
    updateDoc,
    onSnapshot,
    query,
    where,
    orderBy,
    limit,
    serverTimestamp,
    deleteDoc,
    getDocs,
    runTransaction,
    Timestamp,
    type Unsubscribe,
    type WithFieldValue,
} from 'firebase/firestore';
import { db, auth } from '@/services/firebase';
import { logger } from '@/utils/logger';
import { isFirebaseE2EMockEnabled } from '@/utils/e2eMode';
import { getRealAuthenticatedUserId } from '@/utils/authGuards';


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RemoteCommandAttachment {
    url?: string;
    mimeType: string;
    name?: string;
    base64?: string;
}

export interface RemoteCommand {
    id?: string;
    text: string;
    targetAgentId?: string;
    metadata?: Record<string, unknown>;
    /** Optional media attachments (photos, videos, audio) sent with the command */
    attachments?: RemoteCommandAttachment[];
    /**
     * The sole executor allowed to claim this command. Keeping this on the
     * durable command record prevents the Studio listener and Cloud Function
     * from racing to claim the same pending request.
     */
    executionTarget?: RemoteExecutionTarget;
    timestamp: Timestamp | ReturnType<typeof serverTimestamp>;
    // 'cancelled' (ISSUE-989): the phone gave up (timeout/unmount) before the
    // desktop claimed the command — the atomic claim precondition in
    // processSingleCommand() only matches 'pending', so a cancelled command
    // can never be picked up by a later backlog scan/recovery.
    status: 'pending' | 'processing' | 'completed' | 'cancelled';
    createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
}

export interface RemoteResponse {
    id?: string;
    commandId: string;
    text: string;
    agentId?: string;
    imageUrls?: string[];
    videoUrls?: string[];
    isFinal?: boolean;
    timestamp: Timestamp | ReturnType<typeof serverTimestamp>;
    isStreaming: boolean;
    boardroomMessageId?: string;
    rating?: number;
    /** Issue #330: cloud-relay responses declare their advisory runtime. */
    runtime?: string;
}

/** One transport-neutral response contract. Optional keys are omitted, never undefined. */
export function serializeRemoteResponse(input: {
    commandId: string;
    text: string;
    agentId?: string;
    isStreaming?: boolean;
    imageUrls?: string[];
    videoUrls?: string[];
    boardroomMessageId?: string;
}): Omit<RemoteResponse, 'timestamp'> {
    const isStreaming = input.isStreaming === true;
    return {
        commandId: input.commandId,
        text: input.text,
        isStreaming,
        isFinal: !isStreaming,
        ...(input.agentId ? { agentId: input.agentId } : {}),
        ...(input.imageUrls?.length ? { imageUrls: input.imageUrls } : {}),
        ...(input.videoUrls?.length ? { videoUrls: input.videoUrls } : {}),
        ...(input.boardroomMessageId ? { boardroomMessageId: input.boardroomMessageId } : {}),
    };
}

export interface DesktopState {
    currentModule: string;
    isAgentProcessing: boolean;
    activeSessionId: string;
    pendingApproval?: {
        id: string;
        content: string;
        type: string;
        timestamp: number;
        expiresAt: number;
    } | null;
    timestamp: Timestamp | ReturnType<typeof serverTimestamp>;
    online: boolean;
    activeTrackTitle?: string;
    activeTrackId?: string;
    activeTrackArtist?: string;
    isPlaying?: boolean;
    nowPlayingPositionSec?: number;
    nowPlayingDurationSec?: number;
    /** Presence must come from a Studio executor, never the Controller. */
    role?: 'studio';
    /** Per-running-Studio identity, used to distinguish a live executor lease. */
    studioInstanceId?: string;
    /** The Studio has mounted its queue consumer and may safely accept work. */
    listenerReady?: boolean;
    executorDeviceId?: string;
    /** Public compatibility marker; executor credentials are never projected here. */
    protocolVersion?: number;
    /**
     * Capability advertisement (Phase 5). Server-projected from the executor
     * lease holder; the Controller never writes it.
     */
    capabilities?: StudioCapabilities;
    /**
     * True when the desktop is in sleep mode (window hidden to tray, still
     * listening to the relay queue). Lets the phone show Sleeping vs Active vs
     * Offline. Absent/false in the web/PWA build (no Electron tray).
     */
    sleepMode?: boolean;
    /**
     * Local monotonic receipt time of the most recent ADVANCE of the server
     * heartbeat timestamp within this subscription. Set by onDesktopState only
     * when the doc's `timestamp` value changes — never on the initial
     * cache-hit snapshot, whose content may be hours old. Because it is
     * measured with the local clock against local events, it is immune to
     * phone↔server clock skew and is the authoritative freshness signal once
     * at least one live heartbeat has been observed.
     */
    _heartbeatAdvancedAtMs?: number;
}

export type RemoteExecutionTarget = 'cloud' | 'studio';

/**
 * Capabilities the Studio executor advertises so the phone can tell
 * "executor online" apart from "this specific operation is unavailable".
 * Absent/false is always safe: the Controller treats an unknown capability
 * as unavailable rather than assuming the heartbeat implies it.
 */
export interface StudioCapabilities {
    /** Agent pipeline available (listenerReady implies this on a Studio). */
    agent: boolean;
    /** Electron computer-control surface present. */
    computer: boolean;
    /** Local audio engine (analysis/transcode/master) present. */
    audio: boolean;
    /** DAW bridge present. */
    daw: boolean;
    /** Renderer UI available (false while asleep-to-tray). */
    ui: boolean;
}

const LEGACY_STUDIO_COMMAND_PREFIXES = [
    '[GENERATE_IMAGE]',
    '[SHOW]',
    '[WAKE]',
    '[NAVIGATE]',
    '[AGENT_ACTION]',
    '[DAW_CONTROL]',
    '[MEDIA_PLAYBACK]',
    '[RAW]',
] as const;

/**
 * Legacy commands did not record their executor. Preserve those established
 * Studio controls while making all unmarked text cloud-owned. New callers
 * always persist the target, so this fallback is only for already-queued docs.
 */
export function resolveRemoteCommandExecutionTarget(
    command: Pick<RemoteCommand, 'text' | 'executionTarget'>
): RemoteExecutionTarget {
    if (command.executionTarget === 'studio' || command.executionTarget === 'cloud') {
        return command.executionTarget;
    }

    const text = command.text.trim();
    return LEGACY_STUDIO_COMMAND_PREFIXES.some(prefix => text.startsWith(prefix))
        ? 'studio'
        : 'cloud';
}

export interface AgentDispatchTask {
    id?: string;
    type: 'voice_memo' | 'quick_contact' | 'receipt_log' | 'agent_command' | 'live_moment' | 'media_capture' | 'document_scan' | 'venue_log' | 'computer_task' | 'video_render' | 'editor_import';
    payload: {
        audioUrl?: string;
        videoUrl?: string;
        transcription?: string;
        imageUrl?: string;
        mediaType?: 'video' | 'image' | 'audio';
        /** Explicit intent: photos can also be used deliberately in a video timeline. */
        editorDestination?: 'photo' | 'video';
        title?: string;
        amount?: number;
        commandText?: string;
        noteText?: string;
        lat?: number;
        lng?: number;
        accuracyMeters?: number;
        capturedAt?: string;
        /**
         * CE-4, ISSUE-1113: goal-driven computer control dispatched from a remote origin
         * (phone/cloud). Only meaningful for type: 'computer_task'. `constraints` is a plain
         * human-readable string (not structured JSON) so it composes naturally into the
         * agent instruction the desktop executor sends through the normal handshake path —
         * see docs/COMPUTER_EXECUTION_EXTENSION.md §3.5.
         */
        goal?: string;
        constraints?: string;
        /** Canonical persisted editor project requested by queue_video_render. */
        projectId?: string;
        /** Safe basename only; the desktop chooses its managed export directory. */
        outputName?: string;
    };
    status: 'pending' | 'processing' | 'completed' | 'failed';
    executorId?: string;
    createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
    pickedUpAt?: Timestamp | ReturnType<typeof serverTimestamp>;
    completedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
    error?: {
        code: string;
        message: string;
    };
    /**
     * ISSUE-983: the durable receipt proving a note/attachment was actually
     * created — populated only when the desktop executor calls the Notes
     * tool directly and gets a real ID back, never inferred from an agent
     * chat reply.
     */
    result?: {
        noteId?: string;
        assetUrl?: string;
        renderId?: string;
        projectId?: string;
        artworkId?: string;
    };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getUserId(): string | null {
    return getRealAuthenticatedUserId(auth.currentUser);
}

function getRelayRef() {
    if (isFirebaseE2EMockEnabled()) return null;
    const uid = getUserId();
    if (!uid) return null;
    return doc(db, 'users', uid, 'remote-relay', 'state');
}

function getCommandsRef() {
    if (isFirebaseE2EMockEnabled()) return null;
    const uid = getUserId();
    if (!uid) return null;
    return collection(db, 'users', uid, 'remote-relay-commands');
}

function getResponsesRef() {
    if (isFirebaseE2EMockEnabled()) return null;
    const uid = getUserId();
    if (!uid) return null;
    return collection(db, 'users', uid, 'remote-relay-responses');
}

function getDispatchQueueRef() {
    if (isFirebaseE2EMockEnabled()) return null;
    const uid = getUserId();
    if (!uid) return null;
    return collection(db, 'users', uid, 'agent_dispatch_queue');
}

// ---------------------------------------------------------------------------
// Feed scoping
// ---------------------------------------------------------------------------

/**
 * How many of the most-recent docs the conversation feed loads, and how far
 * back in time it looks. This prevents OLD responses (including stale
 * rate-limit / error messages) from resurfacing as if they were current every
 * time the app opens. We query DESC + limit, then re-sort ascending in memory
 * so the UI still renders oldest → newest.
 *
 * NOTE: `where('timestamp', '>=', ...)` combined with `orderBy('timestamp')`
 * acts on the SAME field, so no new composite index is required.
 */
const FEED_PAGE_SIZE = 50;
const FEED_RECENCY_HOURS = 24;
// Background browser tabs throttle setTimeout/setInterval to ~once per minute, so the
// desktop's 5s heartbeat loop collapses to ~60s whenever the studio tab is not focused
// (the common case while driving from a phone). A 15s window made the phone flap between
// connected/reconnecting and eventually unpair. Tolerate throttled beats so the
// pairing holds while the desktop is backgrounded; a genuinely closed desktop is
// still detected within 120s.
export const DESKTOP_HEARTBEAT_STALE_MS = 120_000;
export const DESKTOP_HEARTBEAT_CLOCK_SKEW_TOLERANCE_MS = 30_000;

function getFeedRecencyCutoff(): Timestamp {
    return Timestamp.fromMillis(Date.now() - FEED_RECENCY_HOURS * 60 * 60 * 1000);
}

/**
 * Safely convert a relay doc timestamp to epoch millis for in-memory sorting.
 * Stored docs always carry a resolved Firestore `Timestamp`, but the field type
 * also permits a `serverTimestamp()` sentinel (pre-write); guard for both.
 */
export function relayTimestampToMillis(ts: Timestamp | ReturnType<typeof serverTimestamp> | number | undefined): number {
    if (typeof ts === 'number') return ts;
    if (!ts) return 0;
    // Defensive: handle a plain { toMillis } shape or unresolved sentinel, bypassing instanceof Timestamp issues in tests.
    const maybe = ts as { toMillis?: () => number };
    return typeof maybe?.toMillis === 'function' ? maybe.toMillis() : 0;
}

export function isPrivateIP(hostname: string): boolean {
    if (hostname === 'localhost' || hostname === '127.0.0.1') return true;

    // Class A: 10.X.X.X
    if (hostname.startsWith('10.')) return true;

    // Class C: 192.168.X.X
    if (hostname.startsWith('192.168.')) return true;

    // Class B: 172.16.X.X to 172.31.X.X
    const parts = hostname.split('.');
    if (parts.length === 4 && parts[0] === '172') {
        const second = parseInt(parts[1], 10);
        if (second >= 16 && second <= 31) return true;
    }

    return false;
}

export function isFreshDesktopState(
    state: DesktopState | null | undefined,
    now = Date.now(),
    staleMs = DESKTOP_HEARTBEAT_STALE_MS
): boolean {
    if (!state?.online) return false;

    const timestamp = relayTimestampToMillis(state.timestamp);
    if (timestamp === 0) return false;

    // Once a live heartbeat advance has been witnessed in this subscription,
    // its local receipt time is authoritative: pure wall-clock comparisons
    // against the server timestamp would misjudge a healthy heartbeat when the
    // phone clock drifts, while advancement is skew-proof.
    const advancedAt = state._heartbeatAdvancedAtMs;
    if (typeof advancedAt === 'number') {
        return now - advancedAt <= staleMs;
    }

    // No advance witnessed yet (first snapshot of a subscription). The doc's
    // own heartbeat age must hold on its own — this is what prevents a stale
    // `online:true` doc served from the Firestore cache on every (re)subscribe
    // from masquerading as a fresh heartbeat for a full stale window.
    // Absolute form: a timestamp far in the future means heavy phone-clock
    // lag, which deserves the same distrust as an old one; a witnessed
    // advance (above) is what earns trust back under skew.
    return Math.abs(now - timestamp) <= staleMs + DESKTOP_HEARTBEAT_CLOCK_SKEW_TOLERANCE_MS;
}

/**
 * A generic fresh state document is not enough to call a Controller connected:
 * older Controller builds could write the same document themselves. Only a
 * current Studio executor lease proves that Studio work can be dispatched.
 */
export function isFreshStudioState(
    state: DesktopState | null | undefined,
    now = Date.now(),
    staleMs = DESKTOP_HEARTBEAT_STALE_MS
): boolean {
    return isFreshDesktopState(state, now, staleMs)
        && state?.role === 'studio'
        && typeof state.studioInstanceId === 'string'
        && state.studioInstanceId.length > 0
        && state.listenerReady === true;
}

/**
 * How long the current Studio lease can still be treated as fresh on this
 * device. MobileRemote uses the same boundary as isFreshStudioState so its
 * timeout cannot fire early, briefly recover inside the skew allowance, and
 * then forget to schedule the real stale transition.
 */
export function studioStateFreshnessRemainingMs(
    state: DesktopState | null | undefined,
    now = Date.now(),
    staleMs = DESKTOP_HEARTBEAT_STALE_MS
): number {
    if (!isFreshStudioState(state, now, staleMs)) return 0;

    // Mirror isFreshDesktopState exactly so a scheduled stale timer cannot
    // fire on a different boundary than the predicate it re-checks.
    const advancedAt = state?._heartbeatAdvancedAtMs;
    if (typeof advancedAt === 'number') {
        return Math.max(0, advancedAt + staleMs - now);
    }

    const timestamp = relayTimestampToMillis(state?.timestamp);
    return Math.max(
        0,
        timestamp + staleMs + DESKTOP_HEARTBEAT_CLOCK_SKEW_TOLERANCE_MS - now
    );
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class RemoteRelayService {

    // -----------------------------------------------------------------------
    // PHONE SIDE
    // -----------------------------------------------------------------------

    /**
     * Send a command from the phone. Returns the command document ID.
     */
    async sendCommand(
        text: string,
        targetAgentId?: string,
        metadata?: Record<string, unknown>,
        executionTarget?: RemoteExecutionTarget,
        attachments?: Array<{ url?: string; mimeType: string; name?: string; base64?: string }>
    ): Promise<string | null> {
        const resolvedExecutionTarget = executionTarget ?? resolveRemoteCommandExecutionTarget({ text });

        const ref = getCommandsRef();
        if (!ref) {
            logger.warn('[RemoteRelay] No auth — cannot send command');
            return null;
        }

        const rawAttachments = attachments || (metadata?.attachments as Array<{ url?: string; mimeType: string; name?: string; base64?: string }> | undefined);

        const command: RemoteCommand = {
            text,
            timestamp: serverTimestamp(),
            status: 'pending',
            createdAt: serverTimestamp(),
            ...(targetAgentId ? { targetAgentId } : {}),
            ...(metadata ? { metadata } : {}),
            ...(rawAttachments && rawAttachments.length > 0 ? { attachments: rawAttachments } : {}),
            executionTarget: resolvedExecutionTarget,
        };

        const docRef = await addDoc(ref, command);
        logger.info(`[RemoteRelay] 📱 Command sent: ${docRef.id} → agent: ${targetAgentId || 'auto'}${rawAttachments?.length ? ` with ${rawAttachments.length} attachments` : ''}`);
        return docRef.id;
    }

    /**
     * Dispatch a generic task to the desktop executor (Mobile side).
     *
     * ISSUE-982: callers (QuickCaptureView) treat a resolved promise as success
     * and clear the user's only local copy of the note/media. Silently
     * returning null on missing auth let that happen invisibly. Auth failure
     * must throw so it lands in the caller's existing try/catch instead.
     */
    async dispatchTask(task: Omit<AgentDispatchTask, 'id' | 'status' | 'createdAt'>): Promise<string> {
        if (isFirebaseE2EMockEnabled()) {
            return `e2e-dispatch-${Date.now()}`;
        }

        const ref = getDispatchQueueRef();
        if (!ref) {
            throw new Error('Not authenticated — cannot dispatch task');
        }

        const dispatchDoc: AgentDispatchTask = {
            ...task,
            payload: serializeDispatchPayload(task.payload),
            status: 'pending',
            createdAt: serverTimestamp(),
        };

        const docRef = await addDoc(ref, dispatchDoc);
        logger.info(`[RemoteRelay] 📱 Dispatch task sent: ${docRef.id} [${task.type}]`);
        return docRef.id;
    }

    /**
     * Listen for all dispatch tasks for this user (Mobile side - to see status of tasks).
     */
    onAllDispatchTasks(callback: (tasks: (AgentDispatchTask & { id: string })[]) => void): Unsubscribe {
        const ref = getDispatchQueueRef();
        if (!ref) return () => { };

        // Order by createdAt descending
        const q = query(
            ref,
            orderBy('createdAt', 'desc'),
            limit(50)
        );

        return onSnapshot(q, (snapshot) => {
            const tasks: (AgentDispatchTask & { id: string })[] = [];
            snapshot.forEach((doc) => {
                const data = doc.data() as AgentDispatchTask;
                tasks.push({ ...data, id: doc.id });
            });
            callback(tasks);
        }, (error) => {
            logger.error('[RemoteRelay] onAllDispatchTasks listener error:', error);
        });
    }

    /**
     * Listen for responses to a specific command (phone side).
     * Calls back with each response as it arrives in real-time.
     */
    onResponse(
        commandId: string,
        callback: (response: RemoteResponse) => void
    ): Unsubscribe {
        const ref = getResponsesRef();
        if (!ref) return () => {};

        const q = query(
            ref,
            where('commandId', '==', commandId)
        );

        return onSnapshot(q, (snapshot) => {
            snapshot.docChanges().forEach((change) => {
                if (change.type === 'added' || change.type === 'modified') {
                    const data = change.doc.data() as RemoteResponse;
                    data.id = change.doc.id;
                    callback(data);
                }
            });
        }, (error) => {
            logger.error('[RemoteRelay] Response listener error:', error);
        });
    }

    /**
     * Listen for ALL responses (phone side — for the full conversation feed).
     */
    onAllResponses(callback: (responses: RemoteResponse[]) => void): Unsubscribe {
        const ref = getResponsesRef();
        if (!ref) return () => { };

        // Scope the feed: only the most-recent FEED_PAGE_SIZE docs from the last
        // FEED_RECENCY_HOURS. Order DESC for the limit to grab the newest, then
        // re-sort ascending below so the chat still renders oldest → newest.
        const q = query(
            ref,
            where('timestamp', '>=', getFeedRecencyCutoff()),
            orderBy('timestamp', 'desc'),
            limit(FEED_PAGE_SIZE)
        );

        return onSnapshot(q, (snapshot) => {
            const responses: RemoteResponse[] = [];
            snapshot.forEach((doc) => {
                const data = doc.data() as RemoteResponse;
                data.id = doc.id;
                responses.push(data);
            });
            // Re-sort ascending (oldest → newest) for the UI.
            responses.sort((a, b) => relayTimestampToMillis(a.timestamp) - relayTimestampToMillis(b.timestamp));
            callback(responses);
        }, (error) => {
            logger.error('[RemoteRelay] onAllResponses listener error:', error);
        });
    }

    /**
     * Listen for ALL commands (phone side — for the full conversation feed).
     */
    onAllCommands(callback: (commands: RemoteCommand[]) => void): Unsubscribe {
        const ref = getCommandsRef();
        if (!ref) return () => { };

        // Scope the feed: only the most-recent FEED_PAGE_SIZE docs from the last
        // FEED_RECENCY_HOURS. Order DESC for the limit to grab the newest, then
        // re-sort ascending below so the chat still renders oldest → newest.
        const q = query(
            ref,
            where('timestamp', '>=', getFeedRecencyCutoff()),
            orderBy('timestamp', 'desc'),
            limit(FEED_PAGE_SIZE)
        );

        return onSnapshot(q, (snapshot) => {
            const commands: RemoteCommand[] = [];
            snapshot.forEach((doc) => {
                const data = doc.data() as RemoteCommand;
                data.id = doc.id;
                commands.push(data);
            });
            // Re-sort ascending (oldest → newest) for the UI.
            commands.sort((a, b) => relayTimestampToMillis(a.timestamp) - relayTimestampToMillis(b.timestamp));
            callback(commands);
        }, (error) => {
            logger.error('[RemoteRelay] onAllCommands listener error:', error);
        });
    }

    /**
     * Listen for desktop state changes (phone side).
     */
    onDesktopState(
        callback: (state: DesktopState | null) => void,
        onError?: (error: unknown) => void,
    ): Unsubscribe {
        let unsubFirestore: Unsubscribe = () => {};
        // Heartbeat-advance tracking for THIS subscription. The first snapshot
        // (often the local Firestore cache) only establishes the baseline — it
        // must never certify freshness by itself, because its content can be
        // hours old. Only a later event whose server timestamp VALUE changed
        // proves a live heartbeat, and that observation is stamped with the
        // local monotonic clock so phone↔server skew cannot distort it.
        let lastSeenHeartbeatMs = -1;
        const ref = getRelayRef();
        if (ref) {
            unsubFirestore = onSnapshot(ref, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data({ serverTimestamps: 'estimate' }) as DesktopState;
                    const heartbeatMs = relayTimestampToMillis(data.timestamp);
                    if (heartbeatMs > 0) {
                        if (lastSeenHeartbeatMs !== -1 && heartbeatMs !== lastSeenHeartbeatMs) {
                            data._heartbeatAdvancedAtMs = Date.now();
                        }
                        lastSeenHeartbeatMs = heartbeatMs;
                    }
                    callback(data);
                } else {
                    callback(null);
                }
            }, (error) => {
                logger.error('[RemoteRelay] desktop state listener error:', error);
                onError?.(error);
            });
        }

        return () => {
            unsubFirestore();
        };
    }

    // -----------------------------------------------------------------------
    // DESKTOP SIDE
    // -----------------------------------------------------------------------

    /**
     * Listen for pending commands (desktop side).
     * Fires callback whenever the phone sends a new command.
     */
    onCommand(
        callback: (command: RemoteCommand & { id: string }) => void
    ): Unsubscribe {
        let unsubFirestore: Unsubscribe = () => {};
        const ref = getCommandsRef();
        if (!ref) {
            logger.warn('[RemoteRelay] No Firestore auth — command listener inactive');
        } else {
            logger.info('[RemoteRelay] 🖥️ Starting Firestore command listener...');
            unsubFirestore = onSnapshot(ref, (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added' || change.type === 'modified') {
                        const data = change.doc.data() as RemoteCommand;
                        if (data.status === 'pending') {
                            logger.info(`[RemoteRelay] 📥 Pending command received: ${change.doc.id}`);
                            callback({ ...data, id: change.doc.id });
                        }
                    }
                });
            }, (error) => {
                logger.error('[RemoteRelay] Command listener error:', error);
            });
        }

        return () => {
            unsubFirestore();
        };
    }

    /**
     * Mark a command as completed (desktop side).
     */
    async markCommandCompleted(commandId: string): Promise<void> {
        const uid = getUserId();
        if (!uid) return;

        try {
            if (isFirebaseE2EMockEnabled()) return;
            const { studioExecutorLeaseService } = await import('./StudioExecutorLeaseService');
            await studioExecutorLeaseService.completeCommand(commandId);
        } catch (error) {
            logger.error('[RemoteRelay] markCommandCompleted failed:', error);
        }
    }

    /**
     * Atomically cancel a command if the desktop hasn't claimed it yet
     * (phone side — timeout or giving up on a request).
     *
     * ISSUE-989: a client-side generation timeout previously only detached
     * the phone's listener; the Firestore command stayed 'pending' forever,
     * so a desktop that came back online later (mount/recovery backlog scan)
     * would still execute it — potentially alongside a brand-new retry
     * command, paying for the same generation twice. Returns `true` when the
     * command was genuinely cancelled before being claimed (no cost was
     * incurred); `false` when the desktop already claimed it (work may still
     * be in progress — cannot be cancelled for free) or it doesn't exist.
     */
    async cancelCommand(commandId: string): Promise<boolean> {
        const uid = getUserId();
        if (!uid) return false;

        const ref = doc(db, 'users', uid, 'remote-relay-commands', commandId);
        try {
            return await runTransaction(db, async (tx) => {
                const snap = await tx.get(ref);
                if (snap.exists() && snap.data()?.status === 'pending') {
                    tx.update(ref, { status: 'cancelled' });
                    return true;
                }
                return false;
            });
        } catch (error) {
            logger.error('[RemoteRelay] Command cancel failed:', error);
            return false;
        }
    }

    /**
     * Listen for pending dispatch tasks (desktop side).
     */
    onDispatchTask(
        callback: (task: AgentDispatchTask & { id: string }) => void
    ): Unsubscribe {
        let unsubFirestore: Unsubscribe = () => {};
        const ref = getDispatchQueueRef();
        if (!ref) {
            logger.warn('[RemoteRelay] No Firestore auth — cannot listen for dispatch tasks');
        } else {
            logger.info('[RemoteRelay] 🖥️ Starting Firestore dispatch task listener...');
            unsubFirestore = onSnapshot(ref, (snapshot) => {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added' || change.type === 'modified') {
                        const data = change.doc.data() as AgentDispatchTask;
                        if (data.status === 'pending') {
                            logger.info(`[RemoteRelay] 📥 Pending dispatch task received: ${change.doc.id} [${data.type}]`);
                            callback({ ...data, id: change.doc.id });
                        }
                    }
                });
            }, (error) => {
                logger.error('[RemoteRelay] Dispatch task listener error:', error);
            });
        }
        return unsubFirestore;
    }

    /**
     * Atomically claim a dispatch task by flipping pending → processing
     * inside a Firestore transaction. First caller to commit wins.
     *
     * ISSUE-984: `onDispatchTask` can fire for the same pending task on
     * multiple open desktop listeners (two tabs/windows, or a re-subscribe)
     * before any of them has written back a status change. Without an
     * atomic precondition, every one of them would independently pass and
     * process the same capture — duplicate notes, duplicate AI calls,
     * duplicate spend. The transaction re-reads status inside the commit
     * attempt, so only one caller observes 'pending' and wins the claim.
     */
    async claimDispatchTask(taskId: string): Promise<boolean> {
        const uid = getUserId();
        if (!uid) return false;

        const ref = doc(db, 'users', uid, 'agent_dispatch_queue', taskId);
        try {
            return await runTransaction(db, async (tx) => {
                const snap = await tx.get(ref);
                if (snap.exists() && snap.data()?.status === 'pending') {
                    tx.update(ref, { status: 'processing', pickedUpAt: serverTimestamp() });
                    return true;
                }
                return false;
            });
        } catch (error) {
            logger.error('[RemoteRelay] Dispatch task atomic claim failed:', error);
            return false;
        }
    }

    /**
     * Update the status of a dispatch task (desktop side).
     */
    async updateDispatchTaskStatus(
        taskId: string,
        status: AgentDispatchTask['status'],
        error?: AgentDispatchTask['error'],
        result?: AgentDispatchTask['result']
    ): Promise<void> {
        const uid = getUserId();
        if (!uid) return;

        const updateData: WithFieldValue<Partial<AgentDispatchTask>> = { status };

        if (status === 'processing') {
            updateData.pickedUpAt = serverTimestamp();
        } else if (status === 'completed' || status === 'failed') {
            updateData.completedAt = serverTimestamp();
        }

        if (error) {
            updateData.error = error;
        }

        if (result) {
            updateData.result = result;
        }

        await updateDoc(doc(db, 'users', uid, 'agent_dispatch_queue', taskId), updateData);
        logger.info(`[RemoteRelay] 🖥️ Dispatch task ${taskId} marked as ${status}`);
    }

    /**
     * Listen for status/result changes on a single dispatch task (phone side).
     * ISSUE-983: lets the capture UI wait for a real terminal receipt
     * ('completed' with a noteId, or 'failed') instead of treating queue
     * acceptance itself as success.
     */
    onDispatchTaskUpdate(
        taskId: string,
        callback: (task: AgentDispatchTask & { id: string }) => void,
        onError?: (error: Error) => void
    ): Unsubscribe {
        if (isFirebaseE2EMockEnabled()) return () => {};
        const uid = getUserId();
        if (!uid) throw new Error('Sign in again to check the desktop result.');

        return onSnapshot(
            doc(db, 'users', uid, 'agent_dispatch_queue', taskId),
            (snap) => {
                if (!snap.exists()) return;
                callback({ ...(snap.data() as AgentDispatchTask), id: snap.id });
            },
            (error) => {
                logger.error('[RemoteRelay] Dispatch task update listener error:', error);
                onError?.(error);
            }
        );
    }

    /**
     * Send a response from the desktop (desktop side).
     */
    async sendResponse(
        commandId: string,
        text: string,
        agentId?: string,
        isStreaming = false,
        imageUrls?: string[],
        boardroomMessageId?: string,
        videoUrls?: string[]
    ): Promise<void> {
        const response = serializeRemoteResponse({ commandId, text, agentId, isStreaming, imageUrls, videoUrls, boardroomMessageId });

        // Firestore rejects undefined values (no ignoreUndefinedProperties) —
        // every optional field must be added conditionally, never spread in
        // unconditionally like boardroomMessageId previously was.
        const { studioExecutorLeaseService } = await import('./StudioExecutorLeaseService');
        await studioExecutorLeaseService.publishResponse(response);
        logger.info(`[RemoteRelay] 🖥️ Response sent for command ${commandId} (${text.length} chars, ${imageUrls?.length || 0} images, ${videoUrls?.length || 0} videos)`);
    }

    /**
     * Push desktop state (desktop side).
     */
    async pushDesktopState(state: Omit<DesktopState, 'timestamp'>): Promise<void> {
        if (isFirebaseE2EMockEnabled()) return;
        const { studioExecutorLeaseService } = await import('./StudioExecutorLeaseService');
        await studioExecutorLeaseService.publishPresence(state);
    }

    /**
     * A closing Studio may mark only its own lease offline. Without this guard,
     * a Controller route transition or a second Studio window can overwrite a
     * healthy executor's presence document.
     */
    async releaseStudioPresence(studioInstanceId: string): Promise<void> {
        if (isFirebaseE2EMockEnabled()) return;
        const { studioExecutorLeaseService } = await import('./StudioExecutorLeaseService');
        await studioExecutorLeaseService.releasePresence(studioInstanceId);
    }

    // -----------------------------------------------------------------------
    // CLEANUP
    // -----------------------------------------------------------------------

    /**
     * Delete all commands and responses older than the given age (in hours).
     * Call periodically to prevent unbounded Firestore growth.
     */
    async cleanupOld(maxAgeHours = 24): Promise<number> {
        const uid = getUserId();
        if (!uid) return 0;

        const cutoff = Timestamp.fromMillis(Date.now() - maxAgeHours * 60 * 60 * 1000);
        let deleted = 0;

        // Clean commands
        const cmdsRef = getCommandsRef();
        if (cmdsRef) {
            const q = query(cmdsRef, where('createdAt', '<', cutoff));
            const snap = await getDocs(q);
            for (const d of snap.docs) {
                await deleteDoc(d.ref);
                deleted++;
            }
        }

        // Clean responses
        const resRef = getResponsesRef();
        if (resRef) {
            const q = query(resRef, where('timestamp', '<', cutoff));
            const snap = await getDocs(q);
            for (const d of snap.docs) {
                await deleteDoc(d.ref);
                deleted++;
            }
        }

        if (deleted > 0) {
            logger.info(`[RemoteRelay] Cleaned up ${deleted} old relay documents`);
        }

        return deleted;
    }

    /**
     * Check if user is authenticated (for UI to decide whether to show login).
     */
    isAuthenticated(): boolean {
        return getUserId() !== null;
    }
}

export const remoteRelayService = new RemoteRelayService();

const DISPATCH_CONFIRMATION_TIMEOUT_MS = 90000;

/**
 * ISSUE-983: wait for a dispatch task's real terminal status instead of
 * treating queue acceptance as success — so "Save to Notes" only clears the
 * capture once a note actually exists (or surfaces a genuine failure/timeout).
 */
export function waitForDispatchConfirmation(
    taskId: string
): Promise<{ status: 'completed' | 'failed'; error?: AgentDispatchTask['error']; result?: AgentDispatchTask['result'] }> {
    return new Promise((resolve) => {
        let settled = false;
        let unsubscribe: Unsubscribe = () => {};

        const timeout = setTimeout(() => {
            if (settled) return;
            settled = true;
            unsubscribe();
            resolve({ status: 'failed', error: { code: 'TIMEOUT', message: 'The desktop result is not confirmed. Check Studio before retrying; the task may still finish.' } });
        }, DISPATCH_CONFIRMATION_TIMEOUT_MS);

        const subscribe = () => remoteRelayService.onDispatchTaskUpdate(taskId, (task) => {
            if (task.status !== 'completed' && task.status !== 'failed') return;
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            unsubscribe();
            resolve({ status: task.status, error: task.error, result: task.result });
        }, error => {
            if (settled) return;
            settled = true;
            clearTimeout(timeout);
            unsubscribe();
            resolve({ status: 'failed', error: { code: 'RECEIPT_UNAVAILABLE', message: error.message || 'Cannot confirm the desktop result. Check your connection and sign-in.' } });
        });
        try {
            unsubscribe = subscribe();
            if (settled) unsubscribe();
        } catch (error) {
            settled = true;
            clearTimeout(timeout);
            unsubscribe();
            resolve({ status: 'failed', error: { code: 'RECEIPT_UNAVAILABLE', message: error instanceof Error ? error.message : 'Cannot confirm the desktop result.' } });
        }
    });
}
